import { useEffect, useRef } from 'react'

import { captureFunnelError } from 'helpers/captureFunnelError'
import { TFunnelErrorCode } from 'helpers/funnelErrors'
import { getConnectionInfo } from 'helpers/getConnectionInfo'

const DEFAULT_DELAY_MS = 60000

type TStuckWatchdogParams = {
  isReady: boolean
  code: TFunnelErrorCode
  extra: Record<string, unknown>
  delayMs?: number
}

export const useStuckWatchdog = ({
  isReady,
  code,
  extra,
  delayMs = DEFAULT_DELAY_MS,
}: TStuckWatchdogParams): void => {
  const hasReportedRef = useRef(false)
  // `extra` is a new object on every render — keeping it out of the timer
  // effect's deps, otherwise the timeout restarts and never fires.
  const extraRef = useRef(extra)

  useEffect(() => {
    extraRef.current = extra
  })

  useEffect(() => {
    if (isReady || hasReportedRef.current) return undefined

    const report = (wasHiddenAtTimeout: boolean) => {
      hasReportedRef.current = true

      captureFunnelError(code, null, {
        extra: {
          ...extraRef.current,
          stuckForMs: delayMs,
          elapsedSinceLoadMs: Math.round(performance.now()),
          wasHiddenAtTimeout,
          page: window.location.pathname,
          ...getConnectionInfo(),
        },
      })
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return

      document.removeEventListener('visibilitychange', handleVisibilityChange)
      report(true)
    }

    const timerId = setTimeout(() => {
      if (document.visibilityState === 'visible') {
        report(false)
        return
      }

      document.addEventListener('visibilitychange', handleVisibilityChange)
    }, delayMs)

    return () => {
      clearTimeout(timerId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isReady, code, delayMs])
}
